import {
  CIRCULAR_STATUS_LABEL_PT_BR,
  CircularStatus,
  Permission,
  type CircularListFilter,
} from '@qualicloud/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { CircularStatusBadge } from '@/components/sgq/sgq-badges';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  IconAlertTriangle,
  IconCheckCircle,
  IconInbox,
  IconPlus,
  IconSearch,
  Input,
  Pagination,
  Select,
  Table,
  cn,
  focusRing,
  parseIso,
  type SortState,
  type TableColumn,
} from '@/design-system';
import { circularsApi, queryKeys } from '@/lib/api';
import type { CircularListRow } from '@/lib/api/sgq.types';
import { formatDateTime } from '@/lib/format/datetime';
import { useAuth, useOrganizationTimeZone } from '@/providers/auth-provider';
import type { CircularSearch } from '@/routes/search-schemas';
import { PageBody, PageHeader } from '@/shell/app-shell';

type SortKey = NonNullable<CircularListFilter['sortBy']>;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CIRCULARES — sgq.md §D
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A COLUNA DE COBERTURA é o motivo desta tela existir. A circular não é um
 * comunicado: é a EVIDÊNCIA de que a comunicação alcançou quem devia. Sem a
 * cobertura de leitura, "a equipe foi comunicada" é afirmação sem lastro — e o
 * indicador não existia no sistema legado (§E.6).
 *
 * A circular agora tem SITUAÇÃO. No legado ela nascia publicada, era imutável
 * por AUSÊNCIA de endpoint de edição (BUG-CIR-07), e a única mutação possível
 * era acrescentar destinatários — sem validação nenhuma, por qualquer usuário
 * autenticado que soubesse o id (BUG-CIR-03). Aqui `DRAFT` existe para que um
 * erro de digitação seja corrigível ANTES de a circular virar evidência.
 */
export function CircularsPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const timeZone = useOrganizationTimeZone();
  const search = useSearch({ strict: false }) as unknown as CircularSearch;

  const filter: Partial<CircularListFilter> = useMemo(
    () => ({
      page: search.page ?? 1,
      pageSize: search.pageSize ?? 50,
      status: search.status as CircularStatus[] | undefined,
      scope: (search.scope as CircularListFilter['scope']) ?? 'ALL',
      search: search.search,
      // A URL guarda `AAAA-MM-DD`; o filtro do contrato pede `Date`.
      publishedFrom: parseIso(search.publishedFrom ?? '') ?? undefined,
      publishedTo: parseIso(search.publishedTo ?? '') ?? undefined,
      onlyPendingMyAcknowledgement: search.onlyPendingMyAcknowledgement ?? false,
      sortBy: (search.sortBy as SortKey) ?? 'number',
      sortDir: search.sortDir ?? 'desc',
    }),
    [search],
  );

  const updateFilter = useCallback(
    (patch: Partial<CircularSearch>) => {
      void navigate({
        to: '/circulares',
        search: { ...search, ...patch, page: patch.page ?? 1 } as CircularSearch,
      });
    },
    [navigate, search],
  );

  const listQuery = useQuery({
    queryKey: queryKeys.circulars.list(filter),
    queryFn: () => circularsApi.list(filter),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<Array<TableColumn<CircularListRow, SortKey>>>(
    () => [
      {
        id: 'number',
        header: 'Número',
        sortKey: 'number',
        width: '10%',
        cell: (row) => (
          <Link
            to="/circulares/$circularId"
            params={{ circularId: row.id }}
            className={cn('font-mono text-sm font-medium text-brand hover:underline', focusRing)}
          >
            {row.code}
          </Link>
        ),
      },
      {
        id: 'title',
        header: 'Título',
        sortKey: 'title',
        width: '32%',
        cell: (row) => (
          <div className="min-w-0">
            <p className="truncate text-sm text-content">{row.title}</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              {row.status === CircularStatus.PUBLISHED && !row.acknowledgedByMe ? (
                <Badge tone="warning" size="sm" icon={<IconAlertTriangle size={11} />}>
                  Aguarda sua ciência
                </Badge>
              ) : null}
              {row.acknowledgedByMe ? (
                <Badge tone="success" size="sm" icon={<IconCheckCircle size={11} />}>
                  Ciência registrada
                </Badge>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        id: 'author',
        header: 'Autor',
        width: '15%',
        cell: (row) => <span className="text-sm text-content-muted">{row.authorName ?? '—'}</span>,
      },
      {
        id: 'publishedAt',
        header: 'Publicada em',
        sortKey: 'publishedAt',
        width: '15%',
        cell: (row) => (
          <span className="qc-tabular text-sm text-content-muted">
            {row.publishedAt ? formatDateTime(row.publishedAt, timeZone) : '—'}
          </span>
        ),
      },
      {
        id: 'coverage',
        header: 'Cobertura de leitura',
        width: '18%',
        cell: (row) => <CoverageCell row={row} />,
      },
      {
        id: 'status',
        header: 'Situação',
        width: '10%',
        cell: (row) => <CircularStatusBadge status={row.status} />,
      },
    ],
    [timeZone],
  );

  const [term, setTerm] = useState(search.search ?? '');

  // O campo de busca vive em estado local: limpar só a URL deixaria o termo
  // digitado na tela contradizendo a lista já sem filtro.
  const clearFilters = useCallback(() => {
    setTerm('');
    void navigate({ to: '/circulares', search: { page: 1 } as CircularSearch });
  }, [navigate]);

  const activeFilters = countActiveFilters(search);

  return (
    <>
      <PageHeader
        title="Circulares"
        description="Comunicados internos com ciência assinada. A circular existe para produzir evidência de que a informação chegou — e a cobertura de leitura é essa evidência."
        actions={
          can(Permission.CIRCULAR_MANAGE) ? (
            <Button asChild variant="primary" leadingIcon={<IconPlus size={15} />}>
              <Link to="/circulares/nova">Criar circular</Link>
            </Button>
          ) : null
        }
      />

      <PageBody>
        <Card padding="sm" className="mb-4">
          <section aria-label="Filtros da lista de circulares" className="flex flex-wrap items-end gap-3">
            <form
              role="search"
              className="min-w-[15rem] flex-1"
              onSubmit={(event) => {
                event.preventDefault();
                updateFilter({ search: term.trim() || undefined });
              }}
            >
              <label htmlFor="circular-search" className="qc-sr-only">
                Buscar por número ou título da circular
              </label>
              <Input
                id="circular-search"
                type="search"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder="Buscar por número ou título…"
                leadingIcon={<IconSearch size={15} />}
              />
            </form>

            <div className="w-52">
              <Field label="Situação">
                <Select
                  value={search.status?.[0] ?? '__all__'}
                  onValueChange={(value) =>
                    updateFilter({ status: value === '__all__' ? undefined : [value] })
                  }
                  options={[
                    { value: '__all__', label: 'Todas as situações' },
                    ...Object.values(CircularStatus).map((status) => ({
                      value: status,
                      label: CIRCULAR_STATUS_LABEL_PT_BR[status],
                    })),
                  ]}
                />
              </Field>
            </div>

            <div className="w-52">
              <Field label="Escopo">
                <Select
                  value={search.scope ?? 'ALL'}
                  onValueChange={(value) => updateFilter({ scope: value })}
                  options={[
                    { value: 'ALL', label: 'Todas as que posso ver' },
                    { value: 'AUTHORED', label: 'Que eu emiti' },
                    { value: 'RECEIVED', label: 'Que recebi' },
                  ]}
                />
              </Field>
            </div>

            <Checkbox
              checked={search.onlyPendingMyAcknowledgement ?? false}
              onCheckedChange={(checked) =>
                updateFilter({ onlyPendingMyAcknowledgement: checked || undefined })
              }
              label="Sem minha ciência"
              description="Circulares publicadas que ainda aguardam a sua confirmação de leitura."
            />
          </section>
        </Card>

        <Table<CircularListRow, SortKey>
          caption="Circulares da organização. Colunas: número, título, autor, data de publicação, cobertura de leitura e situação."
          columns={columns}
          rows={listQuery.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={listQuery.isPending}
          sort={{ by: (filter.sortBy ?? 'number') as SortKey, dir: filter.sortDir ?? 'desc' }}
          onSortChange={(sort: SortState<SortKey>) =>
            updateFilter({ sortBy: sort.by, sortDir: sort.dir })
          }
          empty={
            <EmptyState
              icon={<IconInbox size={22} />}
              title={
                activeFilters > 0 ? 'Nenhuma circular corresponde aos filtros' : 'Nenhuma circular'
              }
              description={
                activeFilters > 0
                  ? 'Ajuste ou limpe os filtros para ampliar a busca. Lembre-se de que a lista traz apenas as circulares que você emitiu ou recebeu.'
                  : 'A circular é o comunicado interno cuja leitura precisa ser comprovada. Crie uma para registrar quem foi comunicado, quando e com qual confirmação.'
              }
              action={
                activeFilters > 0 ? (
                  <Button variant="secondary" onClick={clearFilters}>
                    Limpar filtros
                  </Button>
                ) : can(Permission.CIRCULAR_MANAGE) ? (
                  <Button asChild variant="primary">
                    <Link to="/circulares/nova">Criar a primeira circular</Link>
                  </Button>
                ) : undefined
              }
            />
          }
        />

        {listQuery.data && listQuery.data.total > 0 ? (
          <div className="rounded-b-lg border-x border-b border-border bg-surface">
            <Pagination
              page={listQuery.data.page}
              pageSize={listQuery.data.pageSize}
              total={listQuery.data.total}
              itemNoun="circulares"
              onPageChange={(page) => updateFilter({ page })}
              onPageSizeChange={(pageSize) => updateFilter({ pageSize, page: 1 })}
            />
          </div>
        ) : null}
      </PageBody>
    </>
  );
}

function countActiveFilters(search: CircularSearch): number {
  let count = 0;
  if (search.search) count += 1;
  if (search.status?.length) count += 1;
  if (search.scope && search.scope !== 'ALL') count += 1;
  if (search.publishedFrom) count += 1;
  if (search.publishedTo) count += 1;
  if (search.onlyPendingMyAcknowledgement) count += 1;
  return count;
}

/**
 * Cobertura de leitura na célula.
 *
 * O percentual não vai sozinho: vem com a fração absoluta ("5 de 8"), porque
 * "62,5%" numa circular de 8 pessoas e numa de 800 significam esforços de
 * cobrança completamente diferentes. A barra é `aria-hidden`; o texto acessível
 * traz tudo.
 */
function CoverageCell({ row }: { readonly row: CircularListRow }) {
  if (row.status === CircularStatus.DRAFT) {
    return (
      <span className="text-sm text-content-subtle">
        <span aria-hidden="true">—</span>
        <span className="qc-sr-only">
          Rascunho: a circular ainda não foi publicada e não há leitura a apurar.
        </span>
      </span>
    );
  }

  const complete = row.coveragePercent >= 100;

  return (
    <div className="min-w-0">
      <p className="text-sm text-content qc-tabular">
        <span aria-hidden="true">
          {row.coveragePercent.toFixed(1).replace('.', ',')}% ({row.acknowledgedCount}/
          {row.recipientCount})
        </span>
        <span className="qc-sr-only">
          Cobertura de leitura de {row.coveragePercent.toFixed(1).replace('.', ',')} por cento:{' '}
          {row.acknowledgedCount} de {row.recipientCount}{' '}
          {row.recipientCount === 1 ? 'destinatário' : 'destinatários'} com ciência registrada
          {row.pendingCount > 0 ? `, ${row.pendingCount} pendentes` : ''}.
        </span>
      </p>
      <div
        aria-hidden="true"
        className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
      >
        <div
          className={cn(
            'h-full rounded-full',
            complete ? 'bg-success' : row.coveragePercent >= 50 ? 'bg-warning' : 'bg-danger',
          )}
          style={{ width: `${Math.min(100, Math.max(0, row.coveragePercent))}%` }}
        />
      </div>
    </div>
  );
}
